import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, AlertTriangle, CheckCircle, Users, Building, Mail, Phone } from 'lucide-react';

export default function TermsConditionsPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-md' 
          : 'bg-sky-100 backdrop-blur-md border-b border-sky-200 shadow-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                alt="My Corporate Hub" 
                className="h-16 w-auto"
              />
            </Link>
            <Link to="/" className="flex items-center gap-2 text-navy hover:text-gold transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-r from-navy-900 to-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FileText className="w-16 h-16 text-gold mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Terms & Conditions
          </h1>
          <p className="text-xl text-slate-300">
            Please read these terms carefully before using our services
          </p>
          <p className="text-sm text-slate-700 mt-4">
            Last Updated: December 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Quick Overview */}
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm text-center">
              <Scale className="w-8 h-8 text-gold mx-auto mb-3" />
              <h3 className="font-bold text-navy text-sm">Fair Terms</h3>
            </div>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm text-center">
              <Users className="w-8 h-8 text-gold mx-auto mb-3" />
              <h3 className="font-bold text-navy text-sm">User Rights</h3>
            </div>
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm text-center">
              <Building className="w-8 h-8 text-gold mx-auto mb-3" />
              <h3 className="font-bold text-navy text-sm">Clear Guidelines</h3>
            </div>
          </div>

          {/* Detailed Terms */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
            
            {/* Section 1 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">1</span>
                Acceptance of Terms
              </h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                By accessing and using My Corporate Hub ("Platform"), operated by Big Kreation Entertainment Pvt Ltd, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.
              </p>
              <p className="text-slate-700 leading-relaxed">
                These terms apply to all users of the platform, including corporate clients, vendors, hotels, and visitors.
              </p>
            </div>

            {/* Section 2 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">2</span>
                Definitions
              </h2>
              <ul className="space-y-3 text-slate-700">
                <li><strong>"Platform"</strong> refers to My Corporate Hub website and all associated services</li>
                <li><strong>"Client"</strong> refers to corporate users booking venues or services</li>
                <li><strong>"Vendor"</strong> refers to hotels, venues, and service providers listed on the platform</li>
                <li><strong>"Booking"</strong> refers to any reservation made through the platform</li>
                <li><strong>"Services"</strong> refers to MICE (Meetings, Incentives, Conferences, Exhibitions) related offerings</li>
              </ul>
            </div>

            {/* Section 3 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">3</span>
                User Accounts
              </h2>
              <div className="space-y-4 text-slate-700">
                <p><strong>3.1 Registration:</strong> Users must provide accurate, current, and complete information during registration. All accounts require admin approval before activation.</p>
                <p><strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials. Notify us immediately of any unauthorized access.</p>
                <p><strong>3.3 Account Termination:</strong> We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent activity.</p>
              </div>
            </div>

            {/* Section 4 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">4</span>
                Booking Terms
              </h2>
              <div className="space-y-4 text-slate-700">
                <p><strong>4.1 Booking Confirmation:</strong> A booking is confirmed only upon receipt of payment and written confirmation from both the platform and the vendor.</p>
                <p><strong>4.2 Pricing:</strong> All prices are in Indian Rupees (INR) unless otherwise stated. Prices may be subject to taxes and additional charges as specified during booking.</p>
                <p><strong>4.3 Token Payments:</strong> Token payments (typically 20% of total) secure your booking. Remaining balance must be paid as per the agreed payment schedule.</p>
                <p><strong>4.4 Modifications:</strong> Booking modifications are subject to vendor approval and may incur additional charges.</p>
              </div>
            </div>

            {/* Section 5 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">5</span>
                Cancellation & Refunds
              </h2>
              <p className="text-slate-700 mb-4">
                Cancellations are governed by our <Link to="/refund-policy" className="text-gold hover:underline font-semibold">Refund & Cancellation Policy</Link>. Key points include:
              </p>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  30+ days before event: 90% refund
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  15-29 days before event: 50% refund
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  7-14 days before event: 25% refund
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  Less than 7 days: No refund
                </li>
              </ul>
            </div>

            {/* Section 6 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">6</span>
                Payment Terms
              </h2>
              <div className="space-y-4 text-slate-700">
                <p><strong>6.1 Payment Methods:</strong> We accept payments through Razorpay, including Credit Cards, Debit Cards, Net Banking, and UPI.</p>
                <p><strong>6.2 Payment Security:</strong> All transactions are processed securely through Razorpay's PCI DSS compliant payment gateway.</p>
                <p><strong>6.3 Invoice:</strong> GST invoices will be provided for all transactions. GST will be charged as applicable.</p>
                <p><strong>6.4 Currency:</strong> All transactions are processed in Indian Rupees (INR).</p>
              </div>
            </div>

            {/* Section 7 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">7</span>
                Vendor Responsibilities
              </h2>
              <div className="space-y-4 text-slate-700">
                <p><strong>7.1 Service Delivery:</strong> Vendors must provide services as described in their listings and confirmed bookings.</p>
                <p><strong>7.2 Accuracy:</strong> Vendors must maintain accurate and up-to-date information about their properties, availability, and pricing.</p>
                <p><strong>7.3 Communication:</strong> Vendors must respond to inquiries within 24 hours during business hours.</p>
                <p><strong>7.4 Compliance:</strong> Vendors must comply with all applicable laws, regulations, and licensing requirements.</p>
              </div>
            </div>

            {/* Section 8 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">8</span>
                Client Responsibilities
              </h2>
              <div className="space-y-4 text-slate-700">
                <p><strong>8.1 Accurate Information:</strong> Clients must provide accurate event details, guest counts, and requirements.</p>
                <p><strong>8.2 Venue Rules:</strong> Clients must comply with venue rules and regulations during events.</p>
                <p><strong>8.3 Damages:</strong> Clients are responsible for any damages caused to the venue during their event.</p>
                <p><strong>8.4 Permits:</strong> Clients are responsible for obtaining necessary event permits (we can assist through our Event Permits service).</p>
              </div>
            </div>

            {/* Section 9 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">9</span>
                Intellectual Property
              </h2>
              <p className="text-slate-700 mb-4">
                All content on the Platform, including text, graphics, logos, images, and software, is the property of My Corporate Hub or its content suppliers and is protected by intellectual property laws.
              </p>
              <p className="text-slate-700">
                Users may not reproduce, distribute, modify, or create derivative works without explicit written permission.
              </p>
            </div>

            {/* Section 10 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">10</span>
                Limitation of Liability
              </h2>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                    Please read this section carefully as it limits our liability to you.
                  </p>
                </div>
              </div>
              <div className="space-y-4 text-slate-700">
                <p><strong>10.1</strong> My Corporate Hub acts as a platform connecting clients with vendors. We are not liable for the quality, safety, or legality of services provided by vendors.</p>
                <p><strong>10.2</strong> We shall not be liable for any indirect, incidental, special, consequential, or punitive damages.</p>
                <p><strong>10.3</strong> Our total liability shall not exceed the amount paid by you for the specific booking in question.</p>
                <p><strong>10.4</strong> We are not responsible for force majeure events including natural disasters, pandemics, government actions, or other circumstances beyond our control.</p>
              </div>
            </div>

            {/* Section 11 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">11</span>
                Dispute Resolution
              </h2>
              <div className="space-y-4 text-slate-700">
                <p><strong>11.1 Mediation:</strong> In case of disputes, parties agree to first attempt resolution through mediation.</p>
                <p><strong>11.2 Jurisdiction:</strong> These terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in Mumbai, Maharashtra.</p>
                <p><strong>11.3 Arbitration:</strong> Unresolved disputes may be referred to arbitration under the Arbitration and Conciliation Act, 1996.</p>
              </div>
            </div>

            {/* Section 12 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">12</span>
                Prohibited Activities
              </h2>
              <p className="text-slate-700 mb-4">Users are prohibited from:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Using the platform for any unlawful purpose</li>
                <li>Impersonating any person or entity</li>
                <li>Uploading malicious code or attempting to breach security</li>
                <li>Scraping or harvesting data from the platform</li>
                <li>Circumventing the platform to transact directly with vendors</li>
                <li>Posting false reviews or misleading information</li>
                <li>Engaging in any activity that disrupts the platform's operation</li>
              </ul>
            </div>

            {/* Section 13 */}
            <div className="p-8 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">13</span>
                Modifications to Terms
              </h2>
              <p className="text-slate-700">
                We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting to the website. Your continued use of the platform after any changes constitutes acceptance of the new terms. We encourage you to review these terms periodically.
              </p>
            </div>

            {/* Contact Section */}
            <div className="p-8 bg-slate-50">
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm">14</span>
                Contact Information
              </h2>
              <p className="text-slate-700 mb-6">
                For questions about these Terms and Conditions, please contact us:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <a href="mailto:legal@mycorporatehub.com" className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200 hover:border-gold transition-colors">
                  <Mail className="w-6 h-6 text-gold" />
                  <div>
                    <p className="text-sm text-slate-700">Email</p>
                    <p className="font-medium text-navy">legal@mycorporatehub.com</p>
                  </div>
                </a>
                <a href="https://wa.me/919664870766" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200 hover:border-gold transition-colors">
                  <Phone className="w-6 h-6 text-gold" />
                  <div>
                    <p className="text-sm text-slate-700">WhatsApp</p>
                    <p className="font-medium text-navy">+91 96648 70766</p>
                  </div>
                </a>
              </div>
              <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
                <p className="text-sm text-slate-800">
                  <strong>Big Kreation Entertainment Pvt Ltd</strong><br />
                  L23, Express Zone, A Wing, Western Express Highway,<br />
                  Panch Bawadi, Malad East, Mumbai 400063,<br />
                  Maharashtra, India<br />
                  <strong>GST:</strong> 27AAMCB6393G1Z9
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-700 text-sm">
            © 2025 My Corporate Hub. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/privacy-policy" className="text-slate-700 hover:text-white text-sm transition-colors">Privacy Policy</Link>
            <Link to="/refund-policy" className="text-slate-700 hover:text-white text-sm transition-colors">Refund Policy</Link>
            <Link to="/" className="text-slate-700 hover:text-white text-sm transition-colors">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
